import { useState, useEffect, useRef } from "react";

interface CountUpReturn {
    count: string
    ref: React.RefObject<HTMLHeadingElement | null>;
    isAnimating: boolean;
}

const useCountUp = (end: string | number, duration: number = 1000, suffix = "", prefix = ""): CountUpReturn => {
    const [count, setCount] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const ref = useRef<HTMLHeadingElement>(null);

    const parseNumber = (value: string | number): number => {
        if (typeof value === 'number') return value;
        return parseInt(value.replace(/,/g, ''));
    };

    // 숫자에 쉼표를 추가하는 함수
    const addCommas = (num: number): string => {
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };

    // 원본 end 값에 쉼표가 있는지 확인
    const hasCommas = typeof end === 'string' && end.includes(',');
    
    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setIsAnimating(true);
            }
        },
            { threshold: 0.3 }
        );
        const currentElement = ref.current;
        if (currentElement) {
            observer.observe(currentElement);
        }

        return () => {
            if (currentElement) {
                observer.unobserve(currentElement);
            }
        };
    }, []);

    useEffect(() => {
        if (!isAnimating) return;
        
        const startValue = 0;
        const endValue = parseNumber(end);
        const startTime = performance.now();

        const animate = (currentTime: number): void => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            
            const easeOutCubic = 1 - Math.pow(1 - progress, 3);
            const currentCount = Math.floor(startValue + (endValue - startValue) * easeOutCubic);

            setCount(currentCount);

            if (progress < 1) {
                requestAnimationFrame(animate);
            }
        };

        requestAnimationFrame(animate);
    }, [isAnimating, end, duration]);

    return {
        count: `${prefix}${hasCommas ? addCommas(count) : count}${suffix}`,
        ref: ref,
        isAnimating: isAnimating && count < parseNumber(end)
    };
};

export default useCountUp;
